import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Popover, IconButton } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { getAllBodyParts } from "../../../../../common/bodymap/body-parts-data";

const ManagerBodyMapBoy = ({ onUpload, uploadedImages = [] }) => {
  // State management
  const [selectedArea, setSelectedArea] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [frontImageError, setFrontImageError] = useState(false);
  const [backImageError, setBackImageError] = useState(false);

  // Additional states for the persistent popover
  const [clickAnchorEl, setClickAnchorEl] = useState(null);
  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const [clickedPart, setClickedPart] = useState(null);

  // File attachment states
  const [attachments, setAttachments] = useState({});
  const fileInputRef = useRef(null);
  const currentBodyPartRef = useRef(null);

  const bodyParts = getAllBodyParts();

  const handleMouseEnter = (part, event) => {
    // Only show hover popover if no part is clicked
    if (!clickedPart) {
      setHoveredPart(part);
      setHoverAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    // Only clear hover state, not click state
    setHoveredPart(null);
    setHoverAnchorEl(null);
  };

  const handleAreaSelect = (part, event) => {
    setSelectedArea(part);
    // Set the clicked part and anchor element
    setClickedPart(part);
    setClickAnchorEl(event.currentTarget);
    // Store current part id for attachment reference
    currentBodyPartRef.current = part.id;
  };

  const handleCloseClickPopover = () => {
    setClickedPart(null);
    setClickAnchorEl(null);
    currentBodyPartRef.current = null;
  };

  // Handle attachment button click
  const handleAttachmentClick = () => {
    if (currentBodyPartRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const bodyPartId = currentBodyPartRef.current;

    if (files.length > 0 && bodyPartId) {
      const newAttachments = files.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file),
      }));

      setAttachments((prev) => ({
        ...prev,
        [bodyPartId]: [...(prev[bodyPartId] || []), ...newAttachments],
      }));

      // Reset file input value to allow selecting the same file again
      event.target.value = null;
    }
  };

  // Remove an attachment
  const handleRemoveAttachment = (bodyPartId, index) => {
    setAttachments((prev) => {
      const newAttachments = { ...prev };
      if (newAttachments[bodyPartId] && newAttachments[bodyPartId][index]) {
        URL.revokeObjectURL(newAttachments[bodyPartId][index].url); // Clean up URL object
        newAttachments[bodyPartId] = [
          ...newAttachments[bodyPartId].slice(0, index),
          ...newAttachments[bodyPartId].slice(index + 1),
        ];
        if (newAttachments[bodyPartId].length === 0) {
          delete newAttachments[bodyPartId];
        }
      }
      return newAttachments;
    });
  };

  const getUploadedImageData = () => {
    let uploadedData = [];

    Object.entries(attachments).forEach(([bodyPartId, files]) => {
      files.forEach((file) => {
        uploadedData.push({
          bodyPart: bodyParts.find((part) => part.id === bodyPartId)?.name || "Unknown",
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileURL: file.url,
        });
      });
    });

    return uploadedData;
  };

  const parsedUploadedImages = Array.isArray(uploadedImages)
    ? uploadedImages
    : (() => {
        try {
          const parsed = JSON.parse(uploadedImages);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      })();

  useEffect(() => {
    onUpload(getUploadedImageData());
  }, [attachments]); // Calls whenever a new file is added

  return (
    <Box>
      <Typography sx={{ fontWeight: 500, mb: 2 }}>Select the Area of Injury.</Typography>

      {/* Hidden file input */}
      <input
        type='file'
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept='image/*,.pdf,.doc,.docx'
      />

      <Box sx={{ position: "relative", height: 700, width: "100%" }}>
        <svg width='450' height='600' viewBox='0 0 500 550'>
          <image
            href={"/images/Bodymapfront.png"}
            x='40'
            y='0'
            height='600'
            width='225'
            onError={() => setFrontImageError(true)}
          />
          <image
            href={"/images/Bodymapback.png"}
            x='280'
            y='0'
            height='600'
            width='225'
            onError={() => setBackImageError(true)}
          />

          {frontImageError && (
            <text x='185' y='250' textAnchor='middle' fill='red'>
              Front image failed to load
            </text>
          )}
          {backImageError && (
            <text x='325' y='250' textAnchor='middle' fill='red'>
              Back image failed to load
            </text>
          )}

          {bodyParts.map((part) => {
            const [cx, cy, r] = part.coords;
            const hasAttachments = attachments[part.id]?.length > 0;

            return (
              <g key={part.id}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={part.id === selectedArea?.id ? "rgba(25, 118, 210, 0.3)" : "transparent"}
                  stroke={part.id === selectedArea?.id ? "#1976D2" : "transparent"}
                  strokeWidth='2'
                  style={{ cursor: "pointer" }}
                  onClick={(e) => handleAreaSelect(part, e)}
                  onMouseEnter={(e) => handleMouseEnter(part, e)}
                  onMouseLeave={handleMouseLeave}
                />
                {hasAttachments && (
                  <circle cx={cx + r * 0.7} cy={cy - r * 0.7} r='10' fill='#1976D2' stroke='white' strokeWidth='2' />
                )}
              </g>
            );
          })}

          {/* Uploaded Image Previews (Downloadable) */}
          {parsedUploadedImages?.map((file, idx) => {
            const matchedPart = bodyParts.find((bp) => bp.name === file.bodyPart);
            if (!matchedPart) return null;

            const [cx, cy] = matchedPart.coords;
            return (
              <foreignObject key={idx} x={cx - 20} y={cy - 20} width={40} height={40} style={{ pointerEvents: "auto" }}>
                {file.fileType.startsWith("image/") ? (
                  <a
                    href={file.fileURL}
                    download={file.fileName}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "inline-block",
                      textDecoration: "none",
                    }}
                  >
                    <img
                      src={file.fileURL}
                      alt={file.fileName}
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #1976D2",
                        cursor: "pointer",
                      }}
                    />
                  </a>
                ) : (
                  <a href={file.fileURL} download={file.fileName}>
                    <AttachFileIcon style={{ color: "#1976D2", width: 24, height: 24 }} />
                  </a>
                )}
              </foreignObject>
            );
          })}
        </svg>

        {/* Hover tooltip (shows only when no part is clicked) */}
        <Popover
          open={Boolean(hoverAnchorEl) && !clickedPart}
          anchorEl={hoverAnchorEl}
          onClose={handleMouseLeave}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          sx={{
            pointerEvents: "none",
            "& .MuiPaper-root": {
              background: "#1976D2",
              color: "#ffffff",
              px: 2,
              py: 1,
            },
          }}
        >
          <Typography sx={{ color: "white" }}>{hoveredPart?.name}</Typography>
        </Popover>

        {/* Click tooltip (persistent) */}
        <Popover
          open={Boolean(clickAnchorEl)}
          anchorEl={clickAnchorEl}
          onClose={handleCloseClickPopover}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          sx={{
            "& .MuiPaper-root": {
              background: "#1976D2",
              color: "#ffffff",
              px: 2,
              py: 1,
              maxWidth: 300,
            },
          }}
        >
          <Box sx={{ p: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ color: "white", fontWeight: "regular" }}>{clickedPart?.name}</Typography>

              {/* Attachment button in popover */}
              <IconButton
                size='small'
                onClick={handleAttachmentClick}
                sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
              >
                <AttachFileIcon fontSize='small' />
              </IconButton>
            </Box>

            {/* Display attachments for this body part */}
            {clickedPart && attachments[clickedPart.id] && attachments[clickedPart.id].length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ color: "white", fontSize: "0.875rem", mb: 1 }}>
                  Attachments ({attachments[clickedPart.id].length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {attachments[clickedPart.id].map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 1,
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: 80,
                      }}
                    >
                      {file.type.startsWith("image/") ? (
                        <img
                          src={file.url}
                          alt={file.name}
                          style={{ maxWidth: "100%", maxHeight: 60, objectFit: "contain" }}
                        />
                      ) : (
                        <AttachFileIcon sx={{ fontSize: 30, color: "white" }} />
                      )}
                      <Typography
                        variant='caption'
                        sx={{
                          mt: 1,
                          textAlign: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                          whiteSpace: "nowrap",
                          color: "white",
                        }}
                      >
                        {file.name}
                      </Typography>
                      <IconButton
                        size='small'
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          bgcolor: "rgba(0,0,0,0.3)",
                          color: "white",
                          width: 18,
                          height: 18,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
                        }}
                        onClick={() => handleRemoveAttachment(clickedPart.id, index)}
                      >
                        ✕
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default ManagerBodyMapBoy;
